/**
 * TileFallbackBanner (UI-SPEC W4) — top-center status banner shown when the
 * map has flipped to PMTiles fallback. Includes a "Retry primary" link that
 * flips back to OpenFreeMap (with a 60s post-retry grace per
 * tileFallback.createTileFallbackController).
 *
 * Uses logical-property utilities; MapLibre's auto-RTL handling applies to
 * its built-in attribution control, NOT to this overlay (we own the layout).
 */
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertTriangle, X } from "lucide-react";

interface TileFallbackBannerProps {
  onRetryPrimary: () => void;
}

export function TileFallbackBanner({ onRetryPrimary }: TileFallbackBannerProps) {
  const intl = useIntl();
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  // Direction-neutral centering: use a full-width absolute wrapper with
  // flex-center, then the inner pill is centered without relying on
  // `translate-x` (screen-axis math that flips wrong in RTL).
  return (
    <div className="absolute top-4 inset-x-0 z-20 flex justify-center pointer-events-none">
    <div
      role="status"
      data-testid="tile-fallback-banner"
      className="pointer-events-auto bg-warning-container border border-warning border-s-4 rounded-md shadow-md ps-4 pe-4 pbs-3 pbe-3 flex items-center gap-3 max-w-[640px] transition-shadow duration-200"
    >
      <AlertTriangle size={20} className="text-warning shrink-0" aria-hidden="true" />
      <div className="flex-1 flex flex-col">
        <span className="text-sm font-semibold text-warning">
          <FormattedMessage id="WARN_USING_FALLBACK_TILES" />
        </span>
        <span className="text-sm text-warning">
          <FormattedMessage id="LABEL_TILE_FALLBACK_BODY" />
        </span>
      </div>
      <button
        type="button"
        onClick={onRetryPrimary}
        data-testid="tile-fallback-retry-button"
        className="text-sm font-semibold text-warning underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-warning rounded px-2 transition-colors duration-200"
      >
        <FormattedMessage id="LABEL_RETRY_PRIMARY_TILES" />
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={intl.formatMessage({ id: "LABEL_DISMISS" })}
        data-testid="tile-fallback-dismiss-button"
        className="text-warning hover:bg-warning-container/80 rounded p-1 inline-flex items-center justify-center transition-colors duration-200"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
    </div>
  );
}
